import React, { lazy } from "react";

import { FormBuilderFactory } from "./lib/hooks/use-form-builder-factory";
import { defineFormBuilderResolver } from "./lib/utils/define-form-builder-resolver";
import type { InputSelectProps } from "./components/input-select";
import type { InputTextProps } from "./components/input-text";
import type { InputCheckboxProps } from "./components/input-checkbox";
import type { InputTextareaProps } from "./components/input-textarea";
import type { FormSpacerProps } from "./components/form-spacer";
import type { InputRadioProps } from "./components/input-radio";
import type { FormArrayProps } from "./components/form-array";
import type { FormStepperProps } from "./components/form-stepper";
import { ResolverToFormFields } from "./lib/types/form-fields";
import type { FormSectionProps } from "./components/form-section";
import { formFieldToDefaultValues } from "./lib/utils/formFieldToDefaultValues";
import { InputComputedProps } from "./components/input-computed";
import { InputAddressProps } from "./components/input-address";
import { mapResolvers } from "./lib/utils/map-resolvers";
import { InputPhoneProps } from "./components/input-phone";
import { InputHeadlessRadioProps } from "./components/input-headless-radio";
import { InputUserProps } from "../form/components/InputUser";
import { InputMultiSelectProps } from "./components/input-multi-select";

const inputTextResolver = defineFormBuilderResolver<InputTextProps>({
  type: "text",
  component: lazy(() => import("./components/input-text")),
  defaultValue: "",
});

const resolvers = [
  inputTextResolver,
  defineFormBuilderResolver<InputMultiSelectProps>({
    type: "multi-select",
    component: lazy(async () =>
      import("./components/input-multi-select").then((m) => ({
        default: m.InputMultiSelect,
      }))
    ),
    defaultValue: [],
  }),
  defineFormBuilderResolver<InputSelectProps>({
    type: "select",
    component: lazy(async () =>
      import("./components/input-select").then((m) => ({
        default: m.InputSelect,
      }))
    ),
    defaultValue: "",
  }),
  defineFormBuilderResolver<FormSectionProps>({
    type: "section",
    component: lazy(() => import("./components/form-section")),
    defaultValue: ({ options: { fields } }: FormSectionProps) =>
      formFieldToDefaultValues(fields, mapResolvers(resolvers)),
    hasSubFields: true,
  }),
  defineFormBuilderResolver<FormStepperProps>({
    type: "stepper",
    component: lazy(() => import("./components/form-stepper")),
    defaultValue: ({ options: { fields } }: FormStepperProps) =>
      formFieldToDefaultValues(fields, mapResolvers(resolvers)),
    hasSubFields: true,
  }),
  defineFormBuilderResolver<InputCheckboxProps>({
    type: "checkbox",
    component: lazy(() =>
      import("./components/input-checkbox").then((m) => ({
        default: m.InputCheckbox,
      }))
    ),
    defaultValue: [],
  }),
  defineFormBuilderResolver<InputRadioProps>({
    type: "radio",
    component: lazy(() => import("./components/input-radio")),
    defaultValue: [],
  }),
  defineFormBuilderResolver<InputTextareaProps>({
    type: "textarea",
    component: lazy(async () =>
      import("./components/input-textarea").then((m) => ({
        default: m.InputTextarea,
      }))
    ),
    defaultValue: "",
  }),
  defineFormBuilderResolver<InputPhoneProps>({
    type: "phone",
    component: lazy(() => import("./components/input-phone")),
    defaultValue: "",
  }),
  defineFormBuilderResolver<InputAddressProps>({
    type: "address",
    component: lazy(() => import("./components/input-address")),
    defaultValue: null,
  }),
  defineFormBuilderResolver<FormArrayProps>({
    type: "array",
    component: lazy(() => import("./components/form-array")),
    defaultValue: ({ options: { fields, showPlaceholder } }: FormArrayProps) =>
      showPlaceholder
        ? [formFieldToDefaultValues(fields, mapResolvers(resolvers))]
        : [],
    hasSubFields: true,
    isLoopable: true,
  }),
  defineFormBuilderResolver<FormSpacerProps>({
    type: "spacer",
    component: lazy(() => import("./components/form-spacer")),
    defaultValue: null,
  }),
  defineFormBuilderResolver<InputComputedProps>({
    type: "computed",
    component: lazy(() => import("./components/input-computed")),
    defaultValue: "",
  }),

  defineFormBuilderResolver<InputHeadlessRadioProps>({
    type: "headless-radio",
    component: lazy(() => import("./components/input-headless-radio")),
    defaultValue: null,
  }),
  defineFormBuilderResolver<InputUserProps>({
    type: "user",
    component: lazy(() => import("./components/input-user")),
    defaultValue: null,
  }),
  // defineFormBuilderResolver<FormModalArrayProps>({
  //   type: "modal-array",
  //   component: lazy(() => import("./components/form-modal-array.vue")),
  //   defaultValue: () => [],
  //   isLoopable: true,
  // }),
];

export type FormFields = ResolverToFormFields<typeof resolvers>;

export const FormFactory: React.FC<{
  children: React.ReactNode;
}> = (props) => {
  return <FormBuilderFactory resolvers={resolvers} {...props} />;
};
